import { SagaIterator } from 'redux-saga';
import { call, cancel, put, select } from 'redux-saga/effects';
import { getConnectPage } from '../../../../utils/sagaHelpers';
import {
	cancelActiveSagaFlow,
	getPasscode,
	hasSecretBeenDispatchedToDb,
} from '../../../../sharedSelectors/mfa/common';
import { MfaType } from '../../../../utils/mfa';
import { authenticatePasscodeFlow, validatePasscodeFlow } from './validatePasscodeSaga';
import { activateMfaTypeFlow } from './activateMfaTypeSaga';
import { getToken } from 'features/app/redux/context';
import { triggerEmailAuthentication } from '../mfa/slice';

/**
 * Saga validates the email password
 */
export function* emailAuthenticationFlow(): SagaIterator<void> {
	//What page are we hitting this flow from
	const connectPage = yield call(getConnectPage);

	//Has the user decided to cancel mid way through the saga loop
	const cancelSagaStatus = yield select(cancelActiveSagaFlow(connectPage));

	//If user has canceled the flow
	if (cancelSagaStatus) {
		//Reset cancel flag so user can restart flow at discretion
		yield put({
			type: `${connectPage}/setCancelFlow`,
			payload: false,
		});

		//bail out of flow
		yield cancel();
	}

	//Have we already started the saga and have dispatched the request to get a QR code
	const hasSecretBeenSent = yield select(
		hasSecretBeenDispatchedToDb(connectPage, MfaType.EMAIL_VALIDATION)
	);

	//If we are in the Manage Account and we HAVE NOT dispatched the secret
	//ie this is the first time the saga is looping
	if (connectPage === 'mfaSetup' && !hasSecretBeenSent) {
		//Pass in true to enable the Mfa type in the db
		yield call(activateMfaTypeFlow, MfaType.EMAIL_VALIDATION, true, 'EmailAuthentication');
	}

	//Get the passcode the user has just inputted - this can be undefined as the saga might beat them too it
	//will need to be cleared after we have set it to the backend
	const passcode = yield select(getPasscode(connectPage, MfaType.EMAIL_VALIDATION));

	//If the passcode isnt set then the user has not input it yet so cancel and break out of the saga
	if (passcode === undefined) {
		//password hasnt been updated to retrigger saga
		yield put(triggerEmailAuthentication);

		//bail out
		yield cancel();
	}

	//Is the a set up or login attempt
	if (connectPage === 'mfaSetup') {
		//if this is just a step up validate that is has gone ahead
		yield call(validatePasscodeFlow, MfaType.EMAIL_VALIDATION, passcode, 'EmailAuthentication');

		//Trigger mfa set up saga
		//Call Mfa set up saga
		yield put({
			type: `mfa/startSetUp`,
		});
	} else {
		//if its not a set up its the real deal and we need to authenticate it
		const token = yield select(getToken);

		yield call(
			authenticatePasscodeFlow,
			'EmailAuthentication',
			token,
			MfaType.EMAIL_VALIDATION,
			passcode
		);

		//Retrigger execute mfa auth saga (this is technically not needed as email is currently the last one,
		//but leaving here for ease of maintenance if more mfa types are added)
		yield put({
			type: 'context/triggerSagaLoop',
		});
	}
}
